/**
 * La barra de navegación, su menú móvil y el footer, sobre superficies que se
 * leen con cualquier paleta.
 *
 * Los bloques de fábrica los pintaban sobre el acento, de cuando el acento de
 * fábrica era un lima claro con texto oscuro encima. Con las paletas por papel
 * el acento es el color de marca —un azul, un verde vivo— y, encima de él, el
 * texto oscuro no se leía y los enlaces, que van en el acento, desaparecían
 * sobre su propio color. Los bloques nuevos ya salen bien; esto arregla los que
 * ya están en un sitio cuando se cambia de estilo o de paleta.
 *
 * Pasan a sus estilos de sección, «Navegación» y «Footer», sólo si siguen con
 * el fondo con que nacían —el acento de fábrica o, un tiempo, el fondo o la
 * tinta enlazados—. Un color que alguien eligió a propósito se respeta.
 */
import type { ElementNode } from '../store/editor.store'

function isFooter(node: ElementNode) {
  return node.themeRole === 'footer' || node.schemaId === 'site-footer'
}

/* Lo que ahora pone el estilo de sección de la barra y del footer: el fondo, el
   aire —también a los lados— y el borde. */
const OWNED_BY_STYLE = new Set([
  'background', 'backgroundColor', 'backgroundImage',
  'padding', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight',
  'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft', 'borderWidth', 'borderStyle', 'borderColor',
])

/**
 * Quita a la barra y al footer lo que traían guardado de antes y que ahora es
 * de su estilo.
 *
 * La barra nacía con `padding: 12px 24px` propio, que estorbaba a lo que se
 * cambiaba en Style › Secciones. Ahora todo el aire, también el de los lados,
 * es del estilo.
 */
/* El reparto de los hijos: lo que el contenedor viejo hacía por dentro. */
const LAYOUT_KEYS = ['display', 'flexDirection', 'flexWrap', 'alignItems', 'justifyContent', 'gap', 'rowGap', 'columnGap']

/**
 * Saca el contenido de la barra y del footer del contenedor interior con que
 * nacían antes (`nav-container`, `footer-container`).
 *
 * Ese bloque ocupaba toda la sección: al pulsar el footer se elegía él, y como
 * es un bloque, la barra contextual ofrecía estilos de TARJETA en vez de los de
 * sección. Ahora el contenido va suelto en la sección y su ancho lo decide el
 * estilo (Navegación, Footer). El reparto que hacía el contenedor —columna o
 * fila, separación, alineación— pasa a la sección, y su ancho máximo, al ancho
 * de contenido de la sección, para que se vea igual.
 */
function unwrapLegacyContainer(node: ElementNode): ElementNode {
  const innerId = node.behavior?.type === 'nav' ? 'nav-container' : isFooter(node) ? 'footer-container' : null
  if (!innerId || node.type !== 'section') return node
  const index = node.children.findIndex((child) => child.schemaId === innerId)
  const inner = node.children[index]
  if (!inner) return node
  const styles = { ...node.styles } as Record<string, Record<string, string | undefined> | undefined>
  const innerStyles = inner.styles as Record<string, Record<string, string | undefined> | undefined>
  for (const bp of ['desktop', 'tablet', 'mobile'] as const) {
    const innerBag = innerStyles[bp]
    const layout = innerBag ? Object.fromEntries(Object.entries(innerBag).filter(([key, value]) => LAYOUT_KEYS.includes(key) && value !== undefined)) : {}
    if (bp !== 'desktop' && !Object.keys(layout).length) continue
    const own = Object.fromEntries(Object.entries(styles[bp] ?? {}).filter(([key]) => !LAYOUT_KEYS.includes(key)))
    styles[bp] = bp === 'desktop' ? { ...own, width: own.width ?? '100%', ...layout } : { ...own, ...layout }
  }
  const maxWidth = innerStyles.desktop?.maxWidth
  return {
    ...node,
    ...(node.containerWidth === undefined && maxWidth && maxWidth !== 'none' ? { containerWidth: maxWidth } : {}),
    styles: styles as ElementNode['styles'],
    children: [...node.children.slice(0, index), ...inner.children, ...node.children.slice(index + 1)],
  }
}

const TYPED_ARROW = /\s*[▾▼⌄⏷↓]\s*$/

export function releaseStructuralOverrides(node: ElementNode): ElementNode {
  const children = node.children.map(releaseStructuralOverrides)
  let next: ElementNode = children.some((child, index) => child !== node.children[index]) ? { ...node, children } : node
  next = unwrapLegacyContainer(next)
  /* La flecha del desplegable la pinta ya la barra (ver `NAV_CSS`): la que
     venía escrita en el texto —«Productos ▾»— sobra, o saldrían dos. */
  if (node.schemaId === 'nav-dropdown-trigger' && node.content && TYPED_ARROW.test(node.content)) {
    next = { ...next, content: node.content.replace(TYPED_ARROW, '') }
  }
  const section = node.styleRefs?.section
  const owned = (node.behavior?.type === 'nav' && section === 'section-nav') || (isFooter(node) && section === 'section-footer')
  if (!owned) return next
  let changed = false
  const styles = { ...next.styles } as Record<string, Record<string, string | undefined> | undefined>
  for (const bp of ['desktop', 'tablet', 'mobile'] as const) {
    const bag = styles[bp]
    if (!bag || !Object.keys(bag).some((key) => OWNED_BY_STYLE.has(key))) continue
    const kept = Object.fromEntries(Object.entries(bag).filter(([key]) => !OWNED_BY_STYLE.has(key)))
    styles[bp] = kept
    changed = true
  }
  if (changed) next = { ...next, styles: styles as ElementNode['styles'] }
  return next
}

/**
 * Los estilos de sección que son de la barra y del pie, no de una sección de
 * la página. En el modo sencillo no se ofrecen para una sección cualquiera: se
 * eligen sin querer y la sección se pinta como la barra.
 */
export const STRUCTURAL_SECTION_STYLES: Record<'nav' | 'footer', string> = { nav: 'section-nav', footer: 'section-footer' }

export function isStructuralSectionStyle(id: string): boolean {
  return id === STRUCTURAL_SECTION_STYLES.nav || id === STRUCTURAL_SECTION_STYLES.footer
}

export type RebindOptions = {
  /**
   * Al aplicar un estilo de Combinations: la barra también pasa a fija arriba y
   * translúcida al bajar, salvo que ya tuviera otro efecto elegido.
   */
  navBehavior?: boolean
}

/* Los fondos con que nacían la barra y el footer antes de tener estilo propio:
   el acento de fábrica y, un tiempo, el fondo o la tinta enlazados. */
const NAV_FACTORY_FILLS = new Set(['accent', 'background'])
const FOOTER_FACTORY_FILLS = new Set(['accent', 'text'])

export function rebindStructuralSurfaces(node: ElementNode, options: RebindOptions = {}): ElementNode {
  const children = node.children.map((child) => rebindStructuralSurfaces(child, options))
  const colors = node.styleRefs?.colors
  let next: ElementNode = children.some((child, index) => child !== node.children[index]) ? { ...node, children } : node
  /* Pasan a su estilo de sección —«Navegación» o «Footer»—, que pone el fondo:
     se suelta el color de fondo enlazado, que taparía al estilo. */
  const fill = colors?.background ?? ''
  const { background: _background, backgroundColor: _backgroundColor, ...rest } = colors ?? {}
  if (!next.styleRefs?.section) {
    if (node.behavior?.type === 'nav' && (NAV_FACTORY_FILLS.has(fill) || !fill)) {
      next = { ...next, styleRefs: { ...next.styleRefs, section: 'section-nav', card: '', colors: { ...rest, color: 'text' } } }
    } else if (node.schemaId === 'nav-panel' && fill === 'accent') {
      next = { ...next, styleRefs: { ...next.styleRefs, colors: { ...colors, background: 'background', color: 'text' } } }
    } else if (isFooter(node) && FOOTER_FACTORY_FILLS.has(fill)) {
      next = { ...next, styleRefs: { ...next.styleRefs, section: 'section-footer', colors: rest } }
    }
  }
  if (options.navBehavior && node.behavior?.type === 'nav' && (node.behavior.scrollEffect ?? 'none') === 'none') {
    next = { ...next, behavior: { ...node.behavior, sticky: true, scrollEffect: 'blur' } }
  }
  return releaseStructuralOverrides(next)
}
